import { OnDataOptions, useApolloClient, useSubscription } from '@apollo/client';
import { groupBy, isEmpty, isNil } from 'lodash';
import { useEffect } from 'react';
import { Queries, Subscriptions } from '@shared/api';
import { useDraggableContext } from '@shared/scenes/cards/listView/components/DraggableContext';
import { API } from '@shared/types/api';
import { useBoolean } from '@shared/util/hooks';
import { notNil } from '@shared/util/notNil';
import { useCardIsInFilterGroup } from './useCardIsInFilterGroup';
import { useCardMatchesSearchQuery } from './useCardMatchesSearchQuery';
import { useSortedCardList } from './useSortedCardList';

export const CARDS_PER_PAGE = 50;
export const CARDS_PER_GROUP_PAGE = 10;
const TOTAL_RESULTS_COUNT_UPDATE_DELAY_MS = 2500;

export interface UpdateListOptions {
  // additional list-membership predicate for filters that are injected
  // directly into the query variables rather than coming from the controls
  // state (e.g. the tree view's parent filter); a created/updated card is
  // only kept in this list when it passes both this and the controls filters
  cardMatchesList?(entity: API.WorkflowEntity): boolean;
}

export function useUpdateList(
  variables: API.ListWorkflowEntitiesQueryVariables,
  listOptions?: UpdateListOptions,
) {
  const client = useApolloClient();
  const getCardIsInControlsFilterGroup = useCardIsInFilterGroup();
  const getCardMatchesSearchQuery = useCardMatchesSearchQuery();
  const getSortedCards = useSortedCardList();
  const updateCount = useUpdateCount(variables);
  const cardMatchesList = listOptions?.cardMatchesList;
  const getCardIsInFilterGroup = (entity: API.WorkflowEntity) =>
    getCardIsInControlsFilterGroup(entity) && (cardMatchesList?.(entity) ?? true);

  const onUpdate = (options: OnDataOptions<API.WorkflowEntitySubscription>) => {
    if (isNil(options.data.data)) {
      return;
    }

    const { workflowTemplateId } = variables.input;
    const { type, entities: cards, refs } = options.data.data.event;
    const {
      workflowEntityRefs: incomingWorkflowEntityRefs,
      personRefs: incomingPersonRefs,
      recurringEntitiesScheduleRefs: incomingRecurringEntitiesScheduleRefs,
      workflowTemplateRefs: incomingWorkflowTemplateRefs,
    } = refs;

    const getNewCards = () => {
      const groupedCards = groupBy(cards, 'workflowTemplateId');
      return groupedCards[workflowTemplateId];
    };

    const cardGroup = getNewCards();

    // event is not for this workflow template, nothing to update
    if (isEmpty(cardGroup)) {
      return;
    }

    const getExistingCards = () =>
      client.readQuery<API.ListWorkflowEntitiesQuery, API.ListWorkflowEntitiesQueryVariables>({
        query: Queries.workflow.listWorkflowEntities,
        variables,
      });

    const existingCards = getExistingCards();

    // this query is not in the cache, nothing to update
    if (isNil(existingCards)) {
      return;
    }

    const updateList = (data: API.ListWorkflowEntitiesQuery) => {
      client.writeQuery({
        query: Queries.workflow.listWorkflowEntities,
        variables,
        data,
      });
    };

    const {
      workflowEntityRefs: existingWorkflowEntityRefs,
      personRefs: existingPersonRefs,
      recurringEntitiesScheduleRefs: existingRecurringEntitiesScheduleRefs,
      workflowTemplateRefs: existingWorkflowTemplateRefs,
    } = existingCards.list.refs ?? {};

    switch (type) {
      case 'WORKFLOW_ENTITY_CREATED':
      case 'WORKFLOW_ENTITY_RESTORED': {
        updateCount();
        updateList({
          ...existingCards,
          list: {
            ...existingCards.list,
            ...getNewCardsList({
              existingCards,
              incomingCardGroup: cardGroup,
              getCardIsInFilterGroup,
              getCardMatchesSearchQuery,
              getSortedCards,
            }),
            refs: {
              ...existingCards.list.refs,
              workflowEntityRefs: mergeRefs(existingWorkflowEntityRefs, incomingWorkflowEntityRefs),
              personRefs: mergeRefs(existingPersonRefs, incomingPersonRefs),
              recurringEntitiesScheduleRefs: mergeRefs(
                existingRecurringEntitiesScheduleRefs,
                incomingRecurringEntitiesScheduleRefs,
              ),
              workflowTemplateRefs: mergeRefs(
                existingWorkflowTemplateRefs,
                incomingWorkflowTemplateRefs,
              ),
            },
          },
        });

        break;
      }

      case 'WORKFLOW_ENTITY_UPDATED': {
        updateList({
          ...existingCards,
          list: {
            ...existingCards.list,
            ...getNewCardsList({
              existingCards,
              incomingCardGroup: cardGroup,
              getCardIsInFilterGroup,
              getCardMatchesSearchQuery,
              getSortedCards,
            }),
            refs: {
              ...existingCards.list.refs,
              workflowEntityRefs: mergeRefs(existingWorkflowEntityRefs, incomingWorkflowEntityRefs),
              personRefs: mergeRefs(existingPersonRefs, incomingPersonRefs),
              recurringEntitiesScheduleRefs: mergeRefs(
                existingRecurringEntitiesScheduleRefs,
                incomingRecurringEntitiesScheduleRefs,
              ),
              workflowTemplateRefs: mergeRefs(
                existingWorkflowTemplateRefs,
                incomingWorkflowTemplateRefs,
              ),
            },
          },
        });

        break;
      }

      case 'WORKFLOW_ENTITY_DELETED': {
        updateCount();
        let newCards = [...(existingCards.list.entities ?? [])].filter(notNil);
        let totalResults = existingCards.list.totalResults;

        cardGroup.forEach(card => {
          const existingCardIndex = newCards.findIndex(c => c.id === card.id);
          if (existingCardIndex > -1) {
            totalResults = Math.max(0, totalResults - 1);
            newCards = newCards.filter(c => c.id !== card.id);
          }
        });

        updateList({
          ...existingCards,
          list: {
            ...existingCards.list,
            entities: newCards,
            totalResults,
            refs: {
              ...existingCards.list.refs,
              workflowEntityRefs: mergeRefs(existingWorkflowEntityRefs, incomingWorkflowEntityRefs),
              personRefs: mergeRefs(existingPersonRefs, incomingPersonRefs),
              recurringEntitiesScheduleRefs: mergeRefs(
                existingRecurringEntitiesScheduleRefs,
                incomingRecurringEntitiesScheduleRefs,
              ),
              workflowTemplateRefs: mergeRefs(
                existingWorkflowTemplateRefs,
                incomingWorkflowTemplateRefs,
              ),
            },
          },
        });

        break;
      }
    }
  };

  useSubscription<API.WorkflowEntitySubscription>(Subscriptions.workflow.event, {
    onData: onUpdate,
  });
}

function getNewCardsList({
  existingCards,
  incomingCardGroup,
  getCardIsInFilterGroup,
  getCardMatchesSearchQuery,
  getSortedCards,
}: {
  existingCards: API.ListWorkflowEntitiesQuery;
  incomingCardGroup?: API.WorkflowEntity[];
  getCardIsInFilterGroup: (card: API.WorkflowEntity) => boolean;
  getCardMatchesSearchQuery: (
    card: API.WorkflowEntity,
    refs: NonNullable<API.ListWorkflowEntitiesQuery['list']>['refs'] | null,
  ) => boolean;
  getSortedCards: (cards: API.WorkflowEntity[]) => API.WorkflowEntity[];
}): Partial<API.ListWorkflowEntitiesQuery['list']> {
  const existingEntities = existingCards.list.entities ?? [];
  let entities = [...existingEntities].filter(notNil);
  let totalResults = existingCards.list.totalResults;

  incomingCardGroup?.forEach(card => {
    const isCardInFilterGroup = getCardIsInFilterGroup(card);
    const isCardMatchesSearchQuery = getCardMatchesSearchQuery(card, existingCards.list.refs);
    const existingCardIndex = entities.findIndex(c => c.id === card.id);
    const cardExistsInGroup = existingCardIndex > -1;

    // matches filters and search query and is already in group
    if (cardExistsInGroup && isCardInFilterGroup && isCardMatchesSearchQuery) {
      entities[existingCardIndex] = { ...card };

      // matches filters and search query but is not in group
    } else if (!cardExistsInGroup && isCardInFilterGroup && isCardMatchesSearchQuery) {
      totalResults += 1;
      entities = [{ ...card }, ...entities];

      // is in group but doesn't match filters or search query
    } else if (cardExistsInGroup) {
      totalResults = Math.max(0, totalResults - 1);
      entities = [...entities.filter(nc => nc.id !== card.id)];
    }
  });

  return {
    entities: getSortedCards(entities),
    totalResults,
  };
}

function mergeRefs<RefItem extends { id: string | number }>(
  existingRefList: RefItem[] | null | undefined,
  incomingRefList: RefItem[] | null | undefined,
): RefItem[] | null {
  if (isNil(existingRefList) || isNil(incomingRefList)) {
    // if either is null, return the other one, or null
    return existingRefList ?? incomingRefList ?? null;
  }

  // merge refs, using incomingRefList as the source of truth, and re-add any
  // existing refs that are not in the incomingRefList
  const mergedRefList = existingRefList.reduce((memo, existingRef) => {
    if (memo.some(incomingRef => incomingRef.id === existingRef.id)) {
      return memo;
    } else {
      return [...memo, existingRef];
    }
  }, incomingRefList);

  return mergedRefList;
}

function useUpdateCount(variables: API.ListWorkflowEntitiesQueryVariables): VoidFunction {
  const client = useApolloClient();
  const { isDragging } = useDraggableContext();
  const [shouldUpdateCount, updateCount, stopUpdatingCount] = useBoolean(false);

  useEffect(() => {
    if (!shouldUpdateCount || isDragging) {
      return;
    }

    // Update totalResults after any update, after a delay, using limit: 0 to
    // just query for the count. We can't do this immediately because the query
    // hasn't had time to update yet and will return the old count.

    // Also checking for activeCards so we don't bother updating the count while
    // the user is dragging a card. Once they drop the card, totalResults will
    // be updated if necessary.

    // TODO use useCountWorkflowEntities
    const queryForCount = () => {
      client.query({
        query: Queries.workflow.listWorkflowEntities,
        variables: {
          ...variables,
          input: {
            ...variables.input,
            limit: 0,
          },
        },
        fetchPolicy: 'network-only',
      });
      stopUpdatingCount();
    };

    const timer = setTimeout(queryForCount, TOTAL_RESULTS_COUNT_UPDATE_DELAY_MS);
    return () => clearTimeout(timer);
  }, [shouldUpdateCount, isDragging, client, variables, stopUpdatingCount]);

  return updateCount;
}
